import { PORTAL_TYPES } from '@qler-view/shared_lib/src';
import { httpRequest } from '../../shared/lib/utils/httpClient';

export interface SessionFields {
  fieldLabel: string;
  fieldValue: string;
}

export interface ConsultInformation {
  facilityName: string;
  partnerName: string;
  sessionStatus: string;
}

export interface ConsultDetail {
  calculatedWaitTime: string;
  totalWaitTime: string;
  holdTime: string;
  callDuration: string;
  createdOn: string;
  cancelReason: string;
  cancelSubReason: string;
  cancelOtherReason: string;
  cancelPortalType: string;
  onHoldReason: string;
  onHoldSubReason: string;
  onHoldOtherReason: string;
  onHoldPortalType: string;
}

export interface ConsultObservation {
  recommendation: string;
  diagnosis: string;
  emCodes: string;
  isBillableConsult: boolean;
  isSurge: boolean;
  opsComments: string;
  followUp: string;
}

export interface SessionDetails {
  id: number;
  sessionId: string;
  patientName: string;

  consultInformation: ConsultInformation;
  patientInformation: SessionFields[];
  additionalInformation: SessionFields[];
  consultDetail: ConsultDetail;
  consultObservation: ConsultObservation;

  facilityDetails: SessionFields[];

  unreadMessageCount: number;
  pastSessionCount: number;
  sessionDocumentCount: number;
  nextConsultId: number;
  previousConsultId: number;
  notes: string;
}

export interface PastSessionDetails {
  consultId: number;
  createdOn: string;
  providerName: string;
  diagnosis: string;
  recommendation: string;
  sessionStatus: string;
}

export interface PastSession {
  totalCount: number;
  pastSessionRecords: PastSessionDetails[];
}

export interface SuccessResponse {
  code: number;
  message: string;
  status: string;
}
export interface DocType {
  files: File[] | null;
  consultId: number;
  providerId: number;
}

export interface DocumentId {
  documentId: number;
}

export interface SessionDoc {
  fileName: string;
  fileSize: number;
  documentId: number;
  documentUrl: string;
  providerId: number;
  consultId: number;
  facilityId: number;
  patientMrn: number;
  uploadedBy: string;
  uploadedOn: string;
}

export const getOpsConsultMoreDetails = async (
  consultId: number,
  userId: number
): Promise<SessionDetails> => {
  const response = await httpRequest<SessionDetails>({
    method: 'GET',
    url: `/operation/consult/${consultId}/more-details`,
    headers: {
      userId: userId.toString(),
    },
  });
  return response;
};

export const OpsPastSession = async (
  consultId: number
): Promise<PastSession> => {
  const response = await httpRequest<PastSession>({
    url: `/integration/consult/${consultId}/past-session`,
    method: 'GET',
    headers: {
      portalType: PORTAL_TYPES.OPERATIONS,
    },
    apiBaseType: 'INTEGRATION',
  });
  return response;
};

export const getOpsSessionDocument = async (
  consultId: number
): Promise<SessionDoc[]> => {
  const response = await httpRequest<SessionDoc[]>({
    url: `/integration/documents/consult/${consultId}`,
    method: 'GET',
    headers: {
      portalType: PORTAL_TYPES.OPERATIONS,
    },
    apiBaseType: 'INTEGRATION',
  });
  return response;
};

export const opsSessionDownloadDocument = async (
  docId: number
): Promise<string> => {
  const response = await httpRequest<string>({
    url: `/integration/documents/${docId}/download`,
    method: 'GET',
    headers: {
      portalType: PORTAL_TYPES.OPERATIONS,
    },
    apiBaseType: 'INTEGRATION',
  });
  return response;
};

export const uploadSessionDocument = async (
  data: DocType
): Promise<SuccessResponse> => {
  const formData = new FormData();
  if (data.files !== null && data.files.length > 0) {
    data.files.forEach((file) => {
      formData.append(`files`, file);
    });
    formData.append('consultId', `${data.consultId}`);
    formData.append('providerId', `${data.providerId}`);
  } else {
    formData.append('file', '');
    formData.append('consultId', `${data.consultId}`);
    formData.append('providerId', `${data.providerId}`);
  }

  const response = await httpRequest<SuccessResponse>({
    url: `/integration/documents/upload`,
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
      portalType: PORTAL_TYPES.OPERATIONS,
    },
    apiBaseType: 'INTEGRATION',
    data: formData,
  });
  return response;
};

export const getSessionDocument = async (
  consultId: number
): Promise<SessionDoc[]> => {
  const response = await httpRequest<SessionDoc[]>({
    url: `/integration/documents/consult/${consultId}`,
    apiBaseType: 'INTEGRATION',
    method: 'GET',
    headers: {
      portalType: PORTAL_TYPES.OPERATIONS,
    },
  });
  return response;
};
export const sessionDownloadDocument = async (
  docId: number
): Promise<string> => {
  const response = await httpRequest<string>({
    url: `/integration/documents/${docId}/download`,
    apiBaseType: 'INTEGRATION',
    method: 'GET',
    headers: {
      portalType: PORTAL_TYPES.OPERATIONS,
    },
  });
  return response;
};

export const sessionDeleteDocument = async (
  docId: number
): Promise<SuccessResponse> => {
  const response = await httpRequest<SuccessResponse>({
    url: `/integration/documents/${docId}`,
    method: 'DELETE',
    apiBaseType: 'INTEGRATION',
    headers: {
      portalType: PORTAL_TYPES.OPERATIONS,
    },
  });
  return response;
};
